import { Textarea } from "@/components/ui/Textarea";
import { trackEvent } from "@/lib/tracking";
import { useCallback } from "react";
import { useTextareaCursorTracking } from "../hooks/useTextareaCursorTracking";
import { useVoiceRecording } from "../hooks/useVoiceRecording";
import { insertTextAtCursor } from "../utils/insertTextAtCursor";
import { VoiceRecordButton } from "./VoiceRecordButton";

interface DictationTextareaProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof Textarea>,
    "onChange" | "value"
  > {
  value: string;
  onChange: (value: string) => void;
  encounterId?: string;
  disabled?: boolean;
  placeholder?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /**
   * Additional disable conditions for dictation button
   * The button is already disabled during transcription/recording
   */
  disableDictation?: boolean;
  /**
   * Optional tracker ID for recording start event.
   * Pass the appropriate tracker ID for the context where this component is used.
   * Set to undefined to disable tracking.
   */
  trackerStart?: string;
  /**
   * Optional tracker ID for recording end event.
   * Pass the appropriate tracker ID for the context where this component is used.
   * Set to undefined to disable tracking.
   */
  trackerEnd?: string;
}

/**
 * Textarea with built-in voice dictation support
 *
 * Features:
 * - Voice recording with transcription
 * - Smart cursor position tracking
 * - Text insertion at cursor position
 * - Automatic feature flag checking
 * - Conditional rendering of dictation button
 *
 * @example
 * ```tsx
 * // Usage in chat (pass chat-specific trackers)
 * const [text, setText] = useState("");
 * <DictationTextarea
 *   value={text}
 *   onChange={setText}
 *   encounterId={encounterId}
 *   placeholder="Type a message..."
 *   trackerStart={TRACKERS.DICTATION_CHAT_START}
 *   trackerEnd={TRACKERS.DICTATION_CHAT_END}
 * />
 *
 * // No tracking
 * <DictationTextarea
 *   value={text}
 *   onChange={setText}
 *   encounterId={encounterId}
 *   placeholder="Type a message..."
 * />
 * ```
 */
export const DictationTextarea = ({
  value,
  onChange,
  encounterId,
  disabled = false,
  disableDictation = false,
  placeholder,
  onKeyDown,
  className = "",
  trackerStart,
  trackerEnd,
  ...textareaProps
}: DictationTextareaProps) => {
  // Track cursor position for dictation
  const { textareaRef, updateCursorPosition, getCursorPosition } =
    useTextareaCursorTracking();

  // Handle voice recording
  const {
    isRecording,
    isTranscribing,
    permissionDenied,
    handleStartRecording,
    handleStopRecording,
  } = useVoiceRecording({
    encounterId: encounterId,
    onTranscriptionComplete: (text) => {
      // Insert at the cursor position that was saved
      const cursorPosition = getCursorPosition();
      const newText = insertTextAtCursor(value, text, cursorPosition);
      onChange(newText);
    },
    onRecordingStart: () => {
      if (trackerStart) {
        trackEvent(trackerStart);
      }
    },
    onRecordingEnd: () => {
      if (trackerEnd) {
        trackEvent(trackerEnd);
      }
    },
  });

  // Handle text input changes
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  // Determine if voice recording button should be shown
  const showVoiceRecordButton = !disabled;

  // Get placeholder text based on state
  const getPlaceholderText = () => {
    if (isTranscribing) {
      return "Transcribing...";
    }
    if (isRecording) {
      return "Recording...";
    }
    return placeholder;
  };

  return (
    <div className="relative flex-1">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={onKeyDown}
        onClick={updateCursorPosition}
        onKeyUp={updateCursorPosition}
        onSelect={updateCursorPosition}
        placeholder={getPlaceholderText()}
        disabled={disabled || isRecording || isTranscribing}
        className={`${showVoiceRecordButton ? "pr-10" : ""} ${className}`}
        {...textareaProps}
      />
      {showVoiceRecordButton && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
          <VoiceRecordButton
            isRecording={isRecording}
            isTranscribing={isTranscribing}
            permissionDenied={permissionDenied}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            disabled={disableDictation}
          />
        </div>
      )}
    </div>
  );
};
